import { validateAiCostSummary, validateAiUserCost } from './aiCost';
import type { LegalCaseResult } from '../types/legalCase';
import type { AiPricingConfig, AiPricingUpdatePayload, AiCostSummary, AiUserCostDetail } from '../types/adminUser';
import type { CaseData } from '../context/DosarContext.shared';
import type { AnalysisResult } from '../types/analysis';
import type { QueueStatus } from '../types';
import type { Filters } from '../types';
import type { CourtTodayHearingsResponse } from '../types/ecris';
import { fetchSearchResponse } from './searchTransport';
import { searchSessionFetch } from './searchSession';
import type { CourtScope } from './courtScope';

interface SearchParams {
  court_scope?: CourtScope;
  professional_matters?: boolean;
  matter_selection?: MatterSelection;
  situatie: string;
  case_domain?: 'civil' | 'penal';
  materie: string[];
  obiect: string[];
  tip_speta: string[];
  parte: string[];
  offset?: number;
  limit?: number;
  cursor?: string;
  search_id?: string;
  source_case_title?: string;
  search_mode?: 'keywords' | 'factual_semantic' | 'company';
}

const API_URL = '/api';

// Timeout configuration: 3 minutes (180 seconds) for slow LLM responses
const API_TIMEOUT_MS = 180000; // 3 minutes

/**
 * Fetch with timeout support.
 * Wraps the standard fetch API with an AbortController to enforce a timeout.
 *
 * @param url - The URL to fetch
 * @param options - Fetch options (headers, method, body, etc.)
 * @param timeout - Timeout in milliseconds (default: 3 minutes)
 * @returns Promise that resolves to the Response
 */
const fetchWithTimeout = async (
  url: string,
  options: RequestInit = {},
  timeout: number = API_TIMEOUT_MS
): Promise<Response> => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);
  const abortFromCaller = () => controller.abort(options.signal?.reason);
  options.signal?.addEventListener('abort', abortFromCaller, { once: true });

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(id);
    options.signal?.removeEventListener('abort', abortFromCaller);
    return response;
  } catch (error) {
    clearTimeout(id);
    options.signal?.removeEventListener('abort', abortFromCaller);
    if ((error as Error).name === 'AbortError') {
      if (options.signal?.aborted) throw error;
      throw new Error(`Request timeout after ${timeout / 1000} seconds`);
    }
    throw error;
  }
};

export const getFilters = async (): Promise<Filters> => {
  try {
    const response = await fetchWithTimeout(`${API_URL}/filters/menu`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch filters');
    }
    const data = await response.json();

    // Asigură fallback-uri sigure pentru fiecare câmp
    return {
      materii: data.menuData?.materii ?? [],
      obiecte: data.menuData?.obiecte ?? [],
      details: data.menuData?.details ?? {},
      tipSpeta: data.tipSpeta ?? [],
      parte: data.parte ?? [],
    };
  } catch (error) {
    console.error("Error in getFilters:", error);
    // Returnează o structură goală validă în caz de eroare
    return {
      materii: [],
      obiecte: [],
      details: {},
      tipSpeta: [],
      parte: [],
    };
  }
};

export const getFilterMappings = async () => {
  try {
    const response = await fetchWithTimeout(`${API_URL}/search/filters/mappings`, {
      credentials: 'include'
    });
    if (!response.ok) {
      console.warn('Failed to fetch filter mappings');
      return { materii_map: {}, obiecte_map: {} };
    }
    return response.json();
  } catch (error) {
    console.error("Error fetching filter mappings:", error);
    return { materii_map: {}, obiecte_map: {} };
  }
};

export class ApiError extends Error {
  public readonly status: number;
  public code?: string;
  public retryAfter?: number;
  public jobId?: string;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    // Set the prototype explicitly.
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

export const search = async (
  payload: SearchParams,
  signal?: AbortSignal,
  options?: { idempotencyKey?: string }
) => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (options?.idempotencyKey && !(payload.professional_matters && payload.search_id)) {
    headers['Idempotency-Key'] = options.idempotencyKey;
  }

  const response = await fetchSearchResponse(`${API_URL}/search/`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
    credentials: 'include',
    signal,
  }, 60_000, searchSessionFetch);

  if (!response.ok) {
    let detail = `Request failed with status ${response.status}`;
    let code = '';
    let retryAfter = 0;
    let jobId = '';
    try {
      const errorData = await response.json();
      if (typeof errorData.detail === 'object' && errorData.detail !== null) {
        detail = errorData.detail.message || errorData.detail.code || detail;
        code = errorData.detail.code || '';
        retryAfter = errorData.detail.retry_after || 0;
        jobId = errorData.detail.job_id || '';
      } else {
        detail = errorData.detail || detail;
      }
    } catch  {
      // The response body was not JSON.
    }
    const err = new ApiError(detail, response.status);
    err.code = code;
    err.retryAfter = retryAfter;
    err.jobId = jobId;
    throw err;
  }

  const data = await response.json();
  // Handle both array of items and wrapped object { items: [...] } with metadata attached
  if (data && Array.isArray(data.items)) {
    const items = [...data.items];
    return Object.assign(items, {
      items: data.items,
      quota: data.quota,
      job_id: data.job_id,
      search_id: data.search_id,
      is_partial: data.is_partial,
      has_more: data.has_more,
      next_cursor: data.next_cursor,
      role: data.role,
      page_limit: data.page_limit,
      total_count: data.total_count,
    });
  }
  return data;
};

export const getEmbeddingJobStatus = async (jobId: string) => {
  const response = await fetchWithTimeout(`${API_URL}/search/jobs/${encodeURIComponent(jobId)}`, {
    credentials: 'include',
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.detail || 'Eroare la verificarea stării căutării', response.status);
  }
  return response.json();
};

export type MatterSelection = { kind: 'group'; group_id: string } | { kind: 'raw'; raw_value: string };

export interface JurisprudenceMatter {
  selection: MatterSelection;
  raw_values: string[];
  value: string;
  count: number | null;
  available: boolean;
}

export interface JurisprudenceMattersResponse {
  status: 'complete' | 'incomplete';
  matters: JurisprudenceMatter[];
  total: number;
  has_unclassified: boolean;
  truncated?: boolean;
  error?: string;
  search_id?: string;
  iccj_status?: 'FOUND' | 'NOT_FOUND' | 'UNKNOWN' | 'ERROR';
  cedo_status?: 'FOUND' | 'NOT_FOUND' | 'UNKNOWN' | 'ERROR';
  practica_neunitara_status?: 'FOUND' | 'NOT_FOUND' | 'UNKNOWN' | 'ERROR';
  jurnal_practica_judiciara_status?: 'FOUND' | 'NOT_FOUND' | 'UNKNOWN' | 'ERROR';
}

export interface JurisprudenceMattersPayload {
  court_scope?: CourtScope;
  filters?: Record<string, string[]>;
  q?: string;
  situatie?: string;
  search_mode?: string;
  search_id?: string;
}

export const getJurisprudenceMatters = async (
  payload: JurisprudenceMattersPayload,
  signal?: AbortSignal
): Promise<JurisprudenceMattersResponse> => {
  const response = await searchSessionFetch(`${API_URL}/search/jurisprudence/matters`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    let detail = `Request failed with status ${response.status}`;
    try {
      const errorData = await response.json();
      detail = errorData.detail?.message || errorData.detail || detail;
    } catch {
      // ignore
    }
    throw new ApiError(detail, response.status);
  }

  return response.json();
};

export interface JurisprudenceRerankItemResult {
  result_ref: string;
  is_relevant: boolean;
  score: number;
  relevance_band: 'very_high' | 'high' | 'medium' | 'low';
  relevance_category: string;
  reason_code: string;
}

export interface JurisprudenceRerankResponse {
  status: 'ok' | 'fallback';
  reranked: boolean;
  search_mode: string;
  results: JurisprudenceRerankItemResult[];
  kept_refs: string[];
  items?: Record<string, unknown>[] | null;
  acte_juridice: string[];
  candidate_count?: number;
  relevant_count?: number;
  displayed_count?: number;
  display_limit?: number;
  hidden_relevant_count?: number;
  role?: string;
  kept_count: number;
  dropped_count: number;
}

export const rerankJurisprudenceSearchResults = async (
  payload: {
    query: string;
    prepared_query?: string;
    search_mode?: 'keywords' | 'factual_semantic';
    search_id?: string;
    result_refs: string[];
    candidates?: Array<{
      result_ref: string;
      titlu?: string;
      obiect?: string;
      materie?: string;
      snippet?: string;
    }>;
  },
  signal?: AbortSignal,
): Promise<JurisprudenceRerankResponse> => {
  const safePayload = {
    ...payload,
    query: (payload.query || '').trim() || (payload.prepared_query || '').trim() || 'jurisprudenta',
    search_id: payload.search_id || undefined,
    result_refs: (payload.result_refs || []).slice(0, 50),
    candidates: payload.candidates ? payload.candidates.slice(0, 50) : undefined,
  };

  const response = await searchSessionFetch(`${API_URL}/search/jurisprudence/rerank`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(safePayload),
    signal,
  });

  if (!response.ok) {
    let detail = `Request failed with status ${response.status}`;
    try {
      const errorData = await response.json();
      if (typeof errorData?.detail === 'string') {
        detail = errorData.detail;
      } else if (Array.isArray(errorData?.detail)) {
        detail = errorData.detail.map((err: { msg?: string; message?: string }) => err.msg || err.message || JSON.stringify(err)).join(', ');
      } else if (errorData?.detail && typeof errorData.detail === 'object') {
        detail = errorData.detail.message || JSON.stringify(errorData.detail);
      }
    } catch {
      // ignore
    }
    throw new ApiError(detail, response.status);
  }

  return response.json();
};

export function applyJurisprudenceRerank<T extends object>(
  items: T[],
  rerankRes: JurisprudenceRerankResponse,
): T[] {
  if (!rerankRes || !rerankRes.reranked || !Array.isArray(rerankRes.results) || rerankRes.results.length === 0) {
    return items;
  }

  const byRef = new Map<string, T>();
  for (const item of items) {
    const itemObj = item as Record<string, unknown>;
    const ref = String(itemObj.result_ref || itemObj.id || '').trim();
    if (ref && !byRef.has(ref)) {
      byRef.set(ref, item);
    }
  }

  if (Array.isArray(rerankRes.items) && rerankRes.items.length > 0) {
    for (const itm of rerankRes.items) {
      const ref = String(itm.result_ref || itm.id || '').trim();
      if (ref && !byRef.has(ref)) {
        const docFields = (itm.doc && typeof itm.doc === 'object' ? itm.doc : {}) as Record<string, unknown>;
        const hydrated = {
          ...docFields,
          ...itm,
          id: itm.id || docFields.id || ref,
          result_ref: ref,
          ...(itm.data && typeof itm.data === 'object'
            ? { data: itm.data }
            : docFields.data && typeof docFields.data === 'object'
              ? { data: docFields.data }
              : {}),
        };
        byRef.set(ref, hydrated as unknown as T);
      }
    }
  }

  const filteredAndSorted: T[] = [];
  for (const r of rerankRes.results) {
    const original = byRef.get(r.result_ref);
    if (!original) continue;
    const origObj = original as Record<string, unknown>;
    const nestedData =
      origObj.data && typeof origObj.data === 'object'
        ? {
            ...(origObj.data as Record<string, unknown>),
            relevance_band: r.relevance_band,
            relevance_category: r.relevance_category,
            rerank_score: r.score,
            rerank_reason_code: r.reason_code,
            ai_reranked: true,
          }
        : origObj.data;
    filteredAndSorted.push({
      ...original,
      score: r.score,
      rerank_score: r.score,
      rerank_reason_code: r.reason_code,
      relevance_band: r.relevance_band,
      relevance_category: r.relevance_category,
      ai_reranked: true,
      isAISelected: true,
      isCandidateCase: false,
      ...(nestedData ? { data: nestedData } : {}),
    });
  }

  return filteredAndSorted.length > 0 ? filteredAndSorted : items;
}

export const getCaseByRef = async (resultRef: string, signal?: AbortSignal) => {
  const response = await fetchWithTimeout(`${API_URL}/case/by-ref/${encodeURIComponent(resultRef)}`, {
    credentials: 'include',
    signal,
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.detail?.message || errorData.detail || 'Documentul nu a putut fi încărcat', response.status);
  }
  return response.json();
};

export type SectionPreviewStatus = 'ok' | 'empty' | 'expired' | 'missing';

export interface SectionPreviewItem {
  status: SectionPreviewStatus;
  text: string;
}

export interface CaseSectionPreviewResponse {
  section: string;
  previews: Record<string, SectionPreviewItem>;
}

export const getCaseSectionPreviews = async (
  resultRefs: string[],
  section: string,
  signal?: AbortSignal
): Promise<CaseSectionPreviewResponse> => {
  if (!resultRefs || resultRefs.length === 0) {
    return { section, previews: {} };
  }
  const response = await fetchWithTimeout(`${API_URL}/case/section-previews`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      result_refs: resultRefs,
      section,
    }),
    signal,
  });
  if (!response.ok) {
    let detail = 'Eroare la preluarea previzualizării secțiunii';
    let code = '';
    let retryAfter = 0;
    try {
      const errorData = await response.json();
      if (typeof errorData.detail === 'object' && errorData.detail !== null) {
        detail = errorData.detail.message || errorData.detail.code || detail;
        code = errorData.detail.code || '';
        retryAfter = errorData.detail.retry_after || 0;
      } else if (typeof errorData.detail === 'string') {
        detail = errorData.detail;
      }
    } catch  {
      // response not JSON
    }
    const err = new ApiError(detail, response.status);
    err.code = code;
    err.retryAfter = retryAfter;
    throw err;
  }
  return response.json();
};

export const getCaseDetails = async (idOrRef: string | number, signal?: AbortSignal) => {
  const refStr = String(idOrRef).trim();
  const isRef = refStr.startsWith('ref_') || refStr.length >= 24;
  let endpoint = '';
  if (isRef) {
    endpoint = `${API_URL}/case/by-ref/${encodeURIComponent(refStr)}`;
  } else if (/^iccj_\d+$/i.test(refStr)) {
    const numericId = refStr.replace(/^iccj_/i, '');
    endpoint = `${API_URL}/iccj/${encodeURIComponent(numericId)}`;
  } else if (/^speta_\d+$/i.test(refStr)) {
    const numericId = refStr.replace(/^speta_/i, '');
    endpoint = `${API_URL}/case/${encodeURIComponent(numericId)}`;
  } else {
    endpoint = `${API_URL}/case/${encodeURIComponent(refStr)}`;
  }

  const response = await fetchWithTimeout(endpoint, {
    credentials: 'include',
    signal,
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.detail?.message || errorData.detail || 'Detalii caz indisponibile', response.status);
  }
  return response.json();
};


export const advancedSearch = async (payload: SearchParams) => {
  const query = payload.situatie || '*:*';
  const fqList: string[] = [];

  if (payload.materie && payload.materie.length > 0) {
      const materiiStr = payload.materie.map(m => `"${m}"`).join(' OR ');
      fqList.push(`materie:(${materiiStr})`);
  }
  if (payload.obiect && payload.obiect.length > 0) {
      const obiecteStr = payload.obiect.map(o => `"${o}"`).join(' OR ');
      fqList.push(`obiect:(${obiecteStr})`);
  }

  const response = await fetchWithTimeout(`${API_URL}/search/advanced`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      q: query,
      fq: fqList.length > 0 ? fqList.join('||') : null,
      start: payload.offset || 0,
      rows: 20,
      hl: true,
    }),
  });

  if (!response.ok) {
    let detail = `Request failed with status ${response.status}`;
    try {
      const errorData = await response.json();
      detail = errorData.detail || detail;
    } catch  {
      // The response body was not JSON. The default detail is fine.
    }
    throw new ApiError(detail, response.status);
  }

  const data = await response.json();

  // Transform Solr data into the format the UI expects for `results`
  const docs = data.response?.docs || [];
  const highlighting = data.highlighting || {};

  return docs.map((doc: LegalCaseResult) => {
      const hl = highlighting[doc.id] || {};
      const mergedDoc = { ...doc, data: { ...doc } };

      mergedDoc.denumire = doc.titlu || `Caz #${doc.id}`;
      mergedDoc.situatia_de_fapt = doc.text_situatia_de_fapt || "";

      // Inject highlights if present
      if (hl.titlu) mergedDoc.denumire = hl.titlu[0];
      if (hl.parte_introductiva) mergedDoc.data.parte_introductiva = hl.parte_introductiva[0];
      if (hl.considerente) mergedDoc.data.considerente_speta = hl.considerente[0];
      if (hl.dispozitiv) mergedDoc.data.dispozitiv_speta = hl.dispozitiv[0];
      if (hl.analiza_judecator) mergedDoc.data.analiza_judecator = hl.analiza_judecator[0];
      if (hl.argumente_instanta) mergedDoc.data.argumente_instanta = hl.argumente_instanta[0];
      if (hl.text_situatia_de_fapt) mergedDoc.data.text_situatia_de_fapt = hl.text_situatia_de_fapt[0];
      if (hl.text_doctrina) mergedDoc.data.text_doctrina = hl.text_doctrina[0];
      if (hl.text_ce_invatam) mergedDoc.data.text_ce_invatam = hl.text_ce_invatam[0];
      if (hl.text_integral) mergedDoc.data.text_integral = hl.text_integral[0];

      // Aliases for the UI
      mergedDoc.data.situatia_de_fapt = mergedDoc.data.text_situatia_de_fapt;
      mergedDoc.situatia_de_fapt_full = mergedDoc.data.text_situatia_de_fapt || mergedDoc.data.text_integral || "";
      mergedDoc.argumente_instanta = mergedDoc.data.argumente_instanta || "";
      mergedDoc.considerente_speta = mergedDoc.data.considerente_speta || "";
      mergedDoc.text_individualizare = mergedDoc.data.text_individualizare || "";
      mergedDoc.text_doctrina = mergedDoc.data.text_doctrina || "";
      mergedDoc.text_ce_invatam = mergedDoc.data.text_ce_invatam || "";
      mergedDoc.score = 100; // Solr score surrogate

      return mergedDoc;
  });
};

export const searchByIdsPaginated = async (
  ids: (number | string)[],
  page: number = 1,
  pageSize: number = 20
) => {
  if (!ids || ids.length === 0) return [];

  const idString = ids.join(',');
  const hasRef = ids.some(id => typeof id === 'string' && id.startsWith('ref_'));
  const paramKey = hasRef ? 'refs' : 'ids';
  const response = await fetchWithTimeout(
    `${API_URL}/search/by-ids?${paramKey}=${encodeURIComponent(idString)}&page=${page}&page_size=${pageSize}`,
    {
      method: 'GET',
      credentials: 'include'
    }
  );

  if (!response.ok) {
    let detail = `Request failed with status ${response.status}`;
    try {
      const errorData = await response.json();
      detail = errorData.detail || detail;
    } catch  {
      // The response body was not JSON
    }
    throw new ApiError(detail, response.status);
  }

  return response.json();
};

export const searchByIds = async (ids: string) => {
  const hasRef = ids.includes('ref_');
  const paramKey = hasRef ? 'refs' : 'ids';
  const response = await fetchWithTimeout(`${API_URL}/search/by-ids?${paramKey}=${encodeURIComponent(ids)}`, {
    method: 'GET',
    credentials: 'include'
  });

  if (!response.ok) {
    let detail = `Request failed with status ${response.status}`;
    try {
      const errorData = await response.json();
      detail = errorData.detail || detail;
    } catch  {
      // The response body was not JSON. The default detail is fine.
    }
    throw new ApiError(detail, response.status);
  }

  return response.json();
};

export const refreshFilters = async () => {
  const response = await fetchWithTimeout(`${API_URL}/filters/refresh`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Filter refresh failed');
  }
  return response.json();
};

export const predictiveSearch = async (params: SearchParams) => {
  const response = await fetchWithTimeout(`${API_URL}/search/predictive`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
    credentials: 'include'
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Predictive Analysis failed');
  }

  return response.json();
};

export const contribute = async (data: { denumire: string; sursa: string }) => {
  const response = await fetchWithTimeout(`${API_URL}/contribuie/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Contribuie failed');
  }
  return response.json();
};



export const getSettings = async () => {
  const response = await fetchWithTimeout(`${API_URL}/settings/`, {
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error('Failed to fetch settings');
  }
  return response.json();
};

export const updateSettings = async (settings: unknown) => {
  const response = await fetchWithTimeout(`${API_URL}/settings/`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(settings),
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Failed to update settings');
  }
  return response.json();
};

export const resetSettings = async () => {
  const response = await fetchWithTimeout(`${API_URL}/settings/reset`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Failed to reset settings');
  }
  return response.json();
};

export const loginSettings = async (username: string, pass: string) => {
  const response = await fetchWithTimeout(`${API_URL}/settings/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ username, password: pass }),
    credentials: 'include'
  });

  if (!response.ok) {
    let errorDetail = '';
    try {
      const errorData = await response.json();
      errorDetail = errorData?.detail || '';
    } catch {
      // Body is not JSON
    }

    if (response.status === 401) {
      throw new Error(errorDetail || 'Date de autentificare incorecte.');
    }
    if (response.status === 429) {
      const retryAfter = response.headers.get('Retry-After');
      if (retryAfter) {
        throw new Error(`Prea multe încercări. Vă rugăm să așteptați ${retryAfter} secunde înainte de a reîncerca.`);
      }
      throw new Error(errorDetail || 'Prea multe încercări de autentificare. Vă rugăm să reîncercați mai târziu.');
    }
    if (response.status === 503) {
      throw new Error(errorDetail || 'Serviciul de securitate este temporar indisponibil. Reîncercați în câteva momente.');
    }
    throw new Error(errorDetail || 'Eroare la autentificare.');
  }
  return response.json();
};

export const logoutSettings = async () => {
  const response = await fetchWithTimeout(`${API_URL}/settings/logout`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Eroare la deconectare');
  }
  return response.json();
};

export const setMobilePassword = async (new_password: string) => {
  const response = await fetchWithTimeout(`${API_URL}/auth/set-mobile-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ new_password }),
    credentials: 'include'
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Eroare la setarea parolei');
  }
  return response.json();
};

export const precalculateModelsCodes = async (restart: boolean = false) => {
  const response = await fetchWithTimeout(`${API_URL}/settings/precalculate-models-codes?restart=${restart}`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to precalculate models and codes');
  }
  return response.json();
};

export const getPrecalculateStatus = async () => {
  const response = await fetchWithTimeout(`${API_URL}/settings/precalculate-status`, {
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to get precalculate status');
  }
  return response.json();
};

export const stopPrecalculate = async () => {
  const response = await fetchWithTimeout(`${API_URL}/settings/precalculate-stop`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to stop precalculation');
  }
  return response.json();
};

export const precalculateTax = async (restart: boolean = false) => {
  const response = await fetchWithTimeout(`${API_URL}/settings/precalculate-tax?restart=${restart}`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to precalculate tax');
  }
  return response.json();
};

export const getPrecalculateTaxStatus = async () => {
  const response = await fetchWithTimeout(`${API_URL}/settings/precalculate-tax-status`, {
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to get tax precalculate status');
  }
  return response.json();
};

export const stopPrecalculateTax = async () => {
  const response = await fetchWithTimeout(`${API_URL}/settings/precalculate-tax-stop`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to stop tax precalculation');
  }
  return response.json();
};

// Jurisprudence Precalculation API
export const precalculateJurisprudence = async (restart: boolean = false) => {
  const response = await fetchWithTimeout(`${API_URL}/settings/precalculate-jurisprudence?restart=${restart}`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to precalculate jurisprudence');
  }
  return response.json();
};

export const getPrecalculateJurisprudenceStatus = async () => {
  const response = await fetchWithTimeout(`${API_URL}/settings/precalculate-jurisprudence-status`, {
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to get jurisprudence precalculate status');
  }
  return response.json();
};

export const stopPrecalculateJurisprudence = async () => {
  const response = await fetchWithTimeout(`${API_URL}/settings/precalculate-jurisprudence-stop`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to stop jurisprudence precalculation');
  }
  return response.json();
};

// Predictive Reports Precalculation API
export const precalculatePredictive = async (restart: boolean = false) => {
  const response = await fetchWithTimeout(`${API_URL}/settings/precalculate-predictive?restart=${restart}`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to precalculate predictive reports');
  }
  return response.json();
};

export const getPrecalculatePredictiveStatus = async () => {
  const response = await fetchWithTimeout(`${API_URL}/settings/precalculate-predictive-status`, {
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to get predictive precalculate status');
  }
  return response.json();
};

export const stopPrecalculatePredictive = async () => {
  const response = await fetchWithTimeout(`${API_URL}/settings/precalculate-predictive-stop`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to stop predictive precalculation');
  }
  return response.json();
};

// Queue status subscription using Server-Sent Events
export const subscribeToQueueStatus = <T = AnalysisResult>(
  requestId: string,
  onUpdate: (status: QueueStatus & { result?: T; error?: string }) => void,
  onComplete: () => void,
  onError: (error: Error) => void
) => {
  const eventSource = new EventSource(`${API_URL}/queue/status/${requestId}`); // Fixed: Correct SSE endpoint

  eventSource.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);

      if (data.status === 'completed' || data.status === 'error') {
        eventSource.close();
        // Pass final data (which might contain result or error details)
        // to onUpdate before completing.
        onUpdate(data);
        onComplete();
      } else {
        onUpdate(data);
      }
    } catch (e) {
      console.error('Error parsing SSE data:', e);
    }
  };

  eventSource.onerror = () => {
    // console.error('SSE error:', error);
    // Commented out to reduce noise, as browsers often fire error on close
    eventSource.close();
    // Only call onError if it wasn't a clean close
    // onError(new Error('Connection to server lost'));
    onError(new Error('Connection lost'));
  };

  return {
    close: () => eventSource.close()
  };
};

// Feedback API interfaces
export interface FeedbackStats {
  total_feedback: number;
  good_count: number;
  bad_count: number;
  good_percentage: number;
  bad_percentage: number;
}

/**
 * Submit user feedback (good or bad rating)
 */
export const submitFeedback = async (
  feedbackType: 'good' | 'bad',
  spetaId?: number
): Promise<{ success: boolean; message: string }> => {
  const response = await fetchWithTimeout(`${API_URL}/feedback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      feedback_type: feedbackType,
      speta_id: spetaId
    }),
    credentials: 'include'
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to submit feedback');
  }

  return response.json();
};

/**
 * Get feedback statistics
 */
export const getFeedbackStats = async (): Promise<FeedbackStats> => {
  const response = await fetchWithTimeout(`${API_URL}/feedback/stats`, {
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error('Failed to fetch feedback statistics');
  }

  return response.json();
};

// Dosar Search API
export const searchByDosar = async (numarDosar: string, courtSlug?: string) => {
  const payload: { numar_dosar: string; court_slug?: string } = { numar_dosar: numarDosar };
  if (courtSlug) {
    payload.court_slug = courtSlug;
  }
  const response = await searchSessionFetch(`${API_URL}/search-by-dosar`, {
    signal: AbortSignal.timeout(API_TIMEOUT_MS),
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload),
    credentials: 'include'
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to search by dosar number');
  }

  return response.json();
};

export const searchEcrisByCompany = async (companyName: string) => {
  const response = await searchSessionFetch(`${API_URL}/search-by-dosar/company`, {
    signal: AbortSignal.timeout(30000),
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ company_name: companyName }),
    credentials: 'include'
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to search ECRIS by company name');
  }

  return response.json();
};

export const fetchCourtTodayHearings = async (
  courtSlug: string,
  params?: {
    hour?: string | null;
    q?: string | null;
    page?: number;
    page_size?: number;
  }
): Promise<CourtTodayHearingsResponse> => {
  const query = new URLSearchParams();
  if (params?.hour) query.set('hour', params.hour);
  if (params?.q) query.set('q', params.q);
  if (params?.page) query.set('page', String(params.page));
  if (params?.page_size) query.set('page_size', String(params.page_size));

  const qs = query.toString();
  const url = `${API_URL}/ecris-hearings/today/${encodeURIComponent(courtSlug)}${qs ? '?' + qs : ''}`;
  const response = await fetchWithTimeout(url, { method: 'GET' }, 15000);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.message || 'Eroare la obținerea listelor de ședință.');
  }
  return response.json();
};

// User Management API
export const getUsers = async (page = 1, limit = 20, search = '') => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });
  if (search) {
    queryParams.append('search', search);
  }
  const response = await fetchWithTimeout(`${API_URL}/auth/users?${queryParams.toString()}`, {
    method: 'GET',
    credentials: 'include'
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to fetch users');
  }

  return response.json();
};

// Audio Generation API
export const startAudioGeneration = async (restart: boolean = false, type: 'legislation' | 'quiz' | 'flashcards' = 'legislation') => {
  const response = await fetchWithTimeout(`${API_URL}/admin/audio/start?restart=${restart}&type=${type}`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to start audio generation');
  }
  return response.json();
};

export const stopAudioGeneration = async () => {
  const response = await fetchWithTimeout(`${API_URL}/admin/audio/stop`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to stop audio generation');
  }
  return response.json();
};

export const getAudioGenerationStatus = async () => {
  const response = await fetchWithTimeout(`${API_URL}/admin/audio/status`, {
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to get audio generation status');
  }
  return response.json();
};

export const startEncouragementAudioGeneration = async () => {
  const response = await fetchWithTimeout(`${API_URL}/admin/audio/encouragements/start`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to start encouragement audio generation');
  }
  return response.json();
};

export const stopEncouragementAudioGeneration = async () => {
  const response = await fetchWithTimeout(`${API_URL}/admin/audio/encouragements/stop`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to stop encouragement audio generation');
  }
  return response.json();
};

export const getEncouragementAudioGenerationStatus = async () => {
  const response = await fetchWithTimeout(`${API_URL}/admin/audio/encouragements/status`, {
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to get encouragement audio generation status');
  }
  return response.json();
};

// News Articles Audio Generation API
export const startNewsAudioGeneration = async (restart: boolean = false) => {
  const response = await fetchWithTimeout(`${API_URL}/admin/audio/news-articles/start?restart=${restart}`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to start news articles audio generation');
  }
  return response.json();
};

export const stopNewsAudioGeneration = async () => {
  const response = await fetchWithTimeout(`${API_URL}/admin/audio/news-articles/stop`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to stop news articles audio generation');
  }
  return response.json();
};

export const getNewsAudioGenerationStatus = async () => {
  const response = await fetchWithTimeout(`${API_URL}/admin/audio/news-articles/status`, {
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to get news articles audio status');
  }
  return response.json();
};

export const updateUserRole = async (userId: string, role: string) => {
  const response = await fetchWithTimeout(`${API_URL}/auth/users/${userId}/role`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ rol: role }),
    credentials: 'include'
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to update user role');
  }

  return response.json();
};

export const getAdminUserDetails = async (userId: string) => {
  const response = await fetchWithTimeout(`${API_URL}/admin/users/${userId}`, {
    method: 'GET',
    credentials: 'include'
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to fetch user details');
  }
  return response.json();
};

export const updateAdminUser = async (userId: string, data: Record<string, unknown>) => {
  const response = await fetchWithTimeout(`${API_URL}/admin/users/${userId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data),
    credentials: 'include'
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to update user');
  }
  return response.json();
};

export const resetUserPassword = async (userId: string, newPassword: string) => {
  const response = await fetchWithTimeout(`${API_URL}/admin/users/${userId}/reset-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ new_password: newPassword }),
    credentials: 'include'
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to reset password');
  }
  return response.json();
};

// Index Maintenance API
export const getIndexStatus = async () => {
  const response = await fetchWithTimeout(`${API_URL}/settings/index-status`, {
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Failed to get index status');
  }
  return response.json();
};

export const startIndexRepair = async () => {
  const response = await fetchWithTimeout(`${API_URL}/settings/index-repair`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Failed to start index repair');
  }
  return response.json();
};

export const stopIndexRepair = async () => {
  const response = await fetchWithTimeout(`${API_URL}/settings/index-repair/stop`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Failed to stop index repair');
  }
  return response.json();
};

export const checkIndexes = async () => {
  const response = await fetchWithTimeout(`${API_URL}/settings/check-indexes`, {
    method: 'POST',
    credentials: 'include'
  });

  if (!response.ok) throw new Error('Failed to check indexes');
  return response.json();
};

export const checkCompanyIndexes = async () => {
  const response = await fetchWithTimeout(`${API_URL}/settings/check-company-indexes`, {
    method: 'POST',
    credentials: 'include'
  });

  if (!response.ok) throw new Error('Failed to check company indexes');
  return response.json();
};

// Solr Sync API (admin-only)
export const getSolrStatus = async () => {
  const response = await fetchWithTimeout(`${API_URL}/admin/solr-status`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Failed to fetch Solr status');
  return response.json() as Promise<{
    db_spete: number;
    db_firme: number;
    db_total: number;
    solr_total: number;
    gap: number | null;
    needs_sync: boolean;
    solr_reachable: boolean;
  }>;
};

export const triggerSolrSync = async () => {
  const response = await fetchWithTimeout(`${API_URL}/admin/solr-sync`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to trigger Solr sync');
  }
  return response.json() as Promise<{ success: boolean; message: string }>;
};

// Dosar Persistence API
export interface Folder {
  id: number;
  nume: string;
  created_at: string;
  user_id: string;
}

export const getFolders = async (): Promise<Folder[]> => {
  const response = await fetchWithTimeout(`${API_URL}/dosar/folders`, {
    method: 'GET',
    credentials: 'include'
  });
  if (!response.ok) throw new Error('Failed to fetch folders');
  return response.json();
};

export const createFolder = async (nume: string): Promise<Folder> => {
  const response = await fetchWithTimeout(`${API_URL}/dosar/folders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ nume }),
    credentials: 'include'
  });
  if (!response.ok) throw new Error('Failed to create folder');
  return response.json();
};

export const renameFolder = async (folderId: number, data: { nume?: string; prompt_user_request?: string; prompt_fact_situation?: string }): Promise<Folder> => {
  const response = await fetchWithTimeout(`${API_URL}/dosar/folders/${folderId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    credentials: 'include'
  });
  if (!response.ok) throw new Error('Failed to rename folder');
  return response.json();
};

export const deleteFolder = async (folderId: number) => {
  const response = await fetchWithTimeout(`${API_URL}/dosar/folders/${folderId}`, {
    method: 'DELETE',
    credentials: 'include'
  });
  if (!response.ok) throw new Error('Failed to delete folder');
  return response.json();
};

export const getDosarItems = async (folderId?: number | null) => {
  let url = `${API_URL}/dosar/`;
  if (folderId !== undefined && folderId !== null) {
    url += `?folder_id=${folderId}`;
  }

  const response = await fetchWithTimeout(url, {
    method: 'GET',
    credentials: 'include'
  });
  if (!response.ok) throw new Error('Failed to fetch dosar items');
  return response.json();
};

export const addDosarItem = async (item: CaseData, folderId?: number | null) => {
  const payload = { ...item };
  if (folderId) {
    payload.folder_id = folderId;
  }

  const response = await fetchWithTimeout(`${API_URL}/dosar/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to add item to dosar');
  }
  return response.json();
};

export const removeDosarItem = async (vaultItemId: number) => {
  const response = await fetchWithTimeout(`${API_URL}/dosar/items/${vaultItemId}`, {
    method: 'DELETE',
    credentials: 'include'
  });
  if (!response.ok) throw new Error('Failed to remove item from dosar');
  return response.json();
};
// --- Dosar Sharing API ---

export interface ShareResponse {
  id: number;
  dosar_id: number;
  shared_with_email: string;
  permission: 'read' | 'write';
  created_at: string;
}

export interface SharedFolderInfo {
  folder: {
    id: number;
    nume: string;
    created_at: string;
    user_id: string;
  };
  permission: 'read' | 'write';
  owner_name: string;
}

export const shareFolder = async (folderId: number, email: string, permission: 'read' | 'write') => {
  const response = await fetchWithTimeout(`${API_URL}/dosar-share/share`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ folder_id: folderId, email, permission }),
    credentials: 'include'
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to share folder');
  }
  return response.json();
}

export const getFolderShares = async (folderId: number) => {
  const response = await fetchWithTimeout(`${API_URL}/dosar-share/folder/${folderId}/shares`, {
    credentials: 'include'
  });
  if (!response.ok) throw new Error('Failed to fetch folder shares');
  return response.json() as Promise<ShareResponse[]>;
}

export const revokeShare = async (shareId: number) => {
  const response = await fetchWithTimeout(`${API_URL}/dosar-share/share/${shareId}`, {
    method: 'DELETE',
    credentials: 'include'
  });
  if (!response.ok) throw new Error('Failed to revoke share');
  return response.json();
}

export const getSharedWithMeDetails = async () => {
  const response = await fetchWithTimeout(`${API_URL}/dosar-share/shared-with-me`, {
    credentials: 'include'
  });
  if (!response.ok) throw new Error('Failed to fetch shared folders');
  return response.json() as Promise<SharedFolderInfo[]>;
}

// Explanation Audio Generation
export const startExplanationAudioGeneration = async () => {
  const response = await fetchWithTimeout(`${API_URL}/admin/audio/explanations/start`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to start explanation audio generation');
  }
  return response.json();
};

export const stopExplanationAudioGeneration = async () => {
  const response = await fetchWithTimeout(`${API_URL}/admin/audio/explanations/stop`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to stop explanation audio generation');
  }
  return response.json();
};

export const getExplanationAudioGenerationStatus = async () => {
  const response = await fetchWithTimeout(`${API_URL}/admin/audio/explanations/status`, {
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to get explanation audio generation status');
  }
  return response.json();
};
export const deleteUser = async (userId: string) => {
  const response = await fetchWithTimeout(`${API_URL}/auth/users/${userId}`, {
    method: 'DELETE',
    credentials: 'include'
  });

  if (!response.ok) {
    let detail = `Request failed with status ${response.status}`;
    try {
      const errorData = await response.json();
      detail = errorData.detail || detail;
    } catch  {
      // Not JSON
    }
    throw new ApiError(detail, response.status);
  }

  return response.json();
};

export interface PerspectiveResult {
  id?: number;
  result_ref?: string;
  titlu: string;
  act_normativ?: string;
  obiect?: string;
  instanta?: string;
  excerpt: string;
  is_ai: boolean;
  vizualizari?: number;
  cod_sursa?: string;
  is_censored?: boolean;
}

export const getPerspectiveMaterii = async (): Promise<string[]> => {
  const response = await fetchWithTimeout(`${API_URL}/perspective/materii`, {
    credentials: 'include'
  });
  if (!response.ok) throw new Error('Failed to fetch materii');
  return response.json();
};

export const getPerspectiveObiecte = async (materie: string): Promise<string[]> => {
  const response = await fetchWithTimeout(`${API_URL}/perspective/obiecte/${encodeURIComponent(materie)}`, {
    credentials: 'include'
  });
  if (!response.ok) throw new Error('Failed to fetch obiecte');
  return response.json();
};

export const searchPerspective = async (
  materie: string,
  obiect: string,
  contentType: string,
  offset: number = 0,
  limit: number = 50
): Promise<PerspectiveResult[]> => {
  const response = await fetchWithTimeout(`${API_URL}/perspective/search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ materie, obiect, content_type: contentType, offset, limit }),
    credentials: 'include'
  });
  if (!response.ok) throw new Error('Perspective search failed');
  return response.json();
};

// ===== RESET ANDREEA AUDIO =====
// Clears only andreea_audio_file_path from DB without touching Alina audio.
// Use when physical Andreea files were deleted but DB still has stale paths.
export const resetAndreeaAudio = async (): Promise<{ success: boolean; message: string; total_reset: number }> => {
  const response = await fetchWithTimeout(`${API_URL}/admin/audio/reset-andreea`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Eroare necunoscută' }));
    throw new Error(err.detail || 'Resetare Andreea eșuată');
  }
  return response.json();
};

// ===== CUSTOM AUDIO CONVERSION (ANDREEA) =====

export const uploadCustomAudio = async (file: File, pitch: number = 4): Promise<{ success: boolean; message: string; task_id: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('pitch', pitch.toString());

  const response = await fetchWithTimeout(`${API_URL}/admin/audio/custom-convert`, {
    method: 'POST',
    body: formData,
    credentials: 'include'
    // Do NOT set Content-Type header manually when using FormData! The browser sets it with boundary.
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Eroare la încărcare' }));
    throw new Error(err.detail || 'Eroare la încărcarea fișierului');
  }
  return response.json();
}

export const getCustomAudioStatus = async (taskId: string): Promise<{ task_id: string; status: string; original_filename: string; error_message?: string }> => {
  const response = await fetchWithTimeout(`${API_URL}/admin/audio/custom-status/${taskId}`, {
    method: 'GET',
    credentials: 'include'
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Eroare la verificare status' }));
    throw new Error(err.detail || 'Eroare la verificarea statusului');
  }
  return response.json();
}

export const downloadCustomAudioUrl = (taskId: string): string => {
  return `${API_URL}/admin/audio/custom-download/${taskId}`;
}

export const uploadCustomText = async (file: File, pitch: number = 4, voice: string = 'andreea'): Promise<{ success: boolean; message: string; task_id: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('pitch', pitch.toString());
  formData.append('voice', voice);

  const response = await fetchWithTimeout(`${API_URL}/admin/audio/custom-text-convert`, {
    method: 'POST',
    body: formData,
    credentials: 'include'
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Eroare la încărcare text' }));
    throw new Error(err.detail || 'Eroare la încărcarea fișierului text');
  }
  return response.json();
}

// ===== CUSTOM VIDEO CONVERSION (WAV2LIP) =====

export interface Wav2LipParams {
  checkpoint?: string;    // "wav2lip_gan" (sharper visuals) | "wav2lip" (accurate lip-sync, faster)
  box?: string;           // "top bottom left right" — fixed face bbox, bypasses S3FD
  pads?: string;          // "top bottom left right" — padding around auto-detected face
  resize_factor?: number; // 1=native, 2=half, 4=quarter resolution
  nosmooth?: boolean;     // disable temporal bbox smoothing
  rotate?: boolean;       // rotate video right 90° (portrait phone videos)
  crop?: string;          // "top bottom left right" — pre-crop video before processing
  fps?: number;           // output FPS (default 25)
  wav2lip_batch_size?: number;   // model batch size (default 128)
  face_det_batch_size?: number;  // face detection batch size (default 16)
}

export const uploadCustomWav2Lip = async (
  audioFile: File,
  imageFile: File,
  params: Wav2LipParams = {}
): Promise<{ success: boolean; message: string; task_id: string }> => {
  const formData = new FormData();
  formData.append('audio_file', audioFile);
  formData.append('image_file', imageFile);

  if (params.checkpoint) formData.append('checkpoint', params.checkpoint);
  if (params.box) formData.append('box', params.box);
  if (params.pads) formData.append('pads', params.pads);
  if (params.crop) formData.append('crop', params.crop);
  if (params.resize_factor !== undefined) formData.append('resize_factor', String(params.resize_factor));
  if (params.nosmooth !== undefined) formData.append('nosmooth', String(params.nosmooth));
  if (params.rotate !== undefined) formData.append('rotate', String(params.rotate));
  if (params.fps !== undefined) formData.append('fps', String(params.fps));
  if (params.wav2lip_batch_size !== undefined) formData.append('wav2lip_batch_size', String(params.wav2lip_batch_size));
  if (params.face_det_batch_size !== undefined) formData.append('face_det_batch_size', String(params.face_det_batch_size));

  const response = await fetchWithTimeout(`${API_URL}/admin/video/custom-wav2lip`, {
    method: 'POST',
    body: formData,
    credentials: 'include'
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Eroare la încărcare fișiere' }));
    throw new Error(err.detail || 'Eroare la încărcarea fișierelor');
  }
  return response.json();
}


export const getCustomWav2LipStatus = async (taskId: string): Promise<{ task_id: string; status: string; original_filename: string; error_message?: string }> => {
  const response = await fetchWithTimeout(`${API_URL}/admin/video/custom-status/${taskId}`, {
    method: 'GET',
    credentials: 'include'
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Eroare la verificare status' }));
    throw new Error(err.detail || 'Eroare la verificarea statusului');
  }
  return response.json();
}

export const downloadCustomWav2LipUrl = (taskId: string): string => {
  return `${API_URL}/admin/video/custom-download/${taskId}`;
}

// ===== CUSTOM VIDEO LOOP SYNC =====

export const uploadCustomVideoLoop = async (audioFile: File, videoFile: File): Promise<{ success: boolean; message: string; task_id: string }> => {
  const formData = new FormData();
  formData.append('audio_file', audioFile);
  formData.append('video_file', videoFile);

  const response = await fetchWithTimeout(`${API_URL}/admin/video/custom-loop-sync`, {
    method: 'POST',
    body: formData,
    credentials: 'include'
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Eroare la încărcare fișiere' }));
    throw new Error(err.detail || 'Eroare la încărcarea fișierelor video/audio');
  }
  return response.json();
}

export const getCustomVideoLoopStatus = async (taskId: string): Promise<{ task_id: string; status: string; original_filename: string; error_message?: string }> => {
  const response = await fetchWithTimeout(`${API_URL}/admin/video/custom-loop-status/${taskId}`, {
    method: 'GET',
    credentials: 'include'
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Eroare la verificare status' }));
    throw new Error(err.detail || 'Eroare la verificarea statusului video loop');
  }
  return response.json();
}

export const downloadCustomVideoLoopUrl = (taskId: string): string => {
  return `${API_URL}/admin/video/custom-loop-download/${taskId}`;
}

// ===== AVAILABLE RVC VOICES (from admin settings) =====

/**
 * Fetch the list of currently enabled RVC voices from admin settings.
 * Used to dynamically populate voice selectors.
 */
export const getRvcVoicesAvailable = async (): Promise<{ voices: { key: string; label: string }[] }> => {
  const response = await fetchWithTimeout(`${API_URL}/settings/rvc-voices-available`, {
    credentials: 'include',
  });
  if (!response.ok) return { voices: [] };
  return response.json();
};

export interface OutreachEmailPayload {
  sender_email: string;
  recipient_email: string;
  recipient_name: string;
  subject: string;
  body: string;
  attachment_data?: string | null;
  attachment_name?: string | null;
}

export const sendCollaboratorEmail = async (payload: OutreachEmailPayload) => {
  const response = await fetchWithTimeout(`${API_URL}/collaborators/send-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload),
    credentials: 'include'
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.detail || 'Trimiterea emailului a eșuat.', response.status);
  }

  return response.json();
};

export const getCollaboratorHistory = async (page = 1, limit = 10) => {
  const response = await fetchWithTimeout(`${API_URL}/collaborators/history?page=${page}&limit=${limit}`, {
    credentials: 'include'
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.detail || 'Nu s-a putut încărca istoricul.', response.status);
  }

  return response.json();
};

export interface BrevoLogsParams {
  page?: number;
  limit?: number;
  search?: string;
  sender?: string;
  recipient?: string;
  event?: string;
  start_date?: string;
  end_date?: string;
}

export const getBrevoLogs = async (params: BrevoLogsParams = {}) => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.search) queryParams.append('search', params.search);
  if (params.sender) queryParams.append('sender', params.sender);
  if (params.recipient) queryParams.append('recipient', params.recipient);
  if (params.event) queryParams.append('event', params.event);
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);

  const response = await fetchWithTimeout(`${API_URL}/collaborators/brevo-logs?${queryParams.toString()}`, {
    credentials: 'include'
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.detail || 'Nu s-au putut încărca logurile de email.', response.status);
  }

  return response.json();
};

export interface CollaboratorSchedulePayload {
  sender_email: string;
  recipient_email: string;
  recipient_name: string;
  subject: string;
  body: string;
  attachment_data?: string | null;
  attachment_name?: string | null;
  scheduled_time: string;
  timezone_offset_minutes?: number;
}

export interface GetSchedulesParams {
  page?: number;
  limit?: number;
  status_filter?: string;
  recipient?: string;
}

export const scheduleCollaboratorEmail = async (payload: CollaboratorSchedulePayload) => {
  const response = await fetchWithTimeout(`${API_URL}/collaborators/schedule-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    credentials: 'include'
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.detail || 'Nu s-a putut programa emailul.', response.status);
  }

  return response.json();
};

export const getCollaboratorSchedules = async (params: GetSchedulesParams = {}) => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.status_filter) queryParams.append('status_filter', params.status_filter);
  if (params.recipient) queryParams.append('recipient', params.recipient);

  const response = await fetchWithTimeout(`${API_URL}/collaborators/schedules?${queryParams.toString()}`, {
    credentials: 'include'
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.detail || 'Nu s-au putut încărca emailurile programate.', response.status);
  }

  return response.json();
};

export const updateCollaboratorSchedule = async (id: number, payload: CollaboratorSchedulePayload) => {
  const response = await fetchWithTimeout(`${API_URL}/collaborators/schedules/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    credentials: 'include'
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.detail || 'Nu s-a putut actualiza programarea.', response.status);
  }

  return response.json();
};

export const deleteCollaboratorSchedule = async (id: number) => {
  const response = await fetchWithTimeout(`${API_URL}/collaborators/schedules/${id}`, {
    method: 'DELETE',
    credentials: 'include'
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.detail || 'Nu s-a putut șterge/anula programarea.', response.status);
  }

  return response.json();
};


// ─── Followed Cases API ───────────────────────────────────────────────────

export const followCase = async (caseNumber: string) => {
  const response = await fetchWithTimeout(`${API_URL}/followed-cases/follow`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ case_number: caseNumber }),
    credentials: 'include'
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.detail || 'Nu s-a putut urmări dosarul.', response.status);
  }

  return response.json();
};

export const unfollowCase = async (caseNumber: string) => {
  const response = await fetchWithTimeout(`${API_URL}/followed-cases/unfollow`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ case_number: caseNumber }),
    credentials: 'include'
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.detail || 'Nu s-a putut dezactiva urmărirea dosarului.', response.status);
  }

  return response.json();
};

export const getFollowStatus = async (caseNumber: string) => {
  const response = await fetchWithTimeout(`${API_URL}/followed-cases/status?case_number=${encodeURIComponent(caseNumber)}`, {
    credentials: 'include'
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.detail || 'Nu s-a putut obține starea de urmărire a dosarului.', response.status);
  }

  return response.json();
};

export interface HearingReminderState {
  available_reminder_days: number[];
  hearing_reminders: Array<{ days_before: number; hearing_date: string; due_at: string }>;
}

export const setHearingReminder = async (caseNumber: string, daysBefore: number, enabled: boolean): Promise<HearingReminderState> => {
  const response = await fetchWithTimeout(`${API_URL}/followed-cases/hearing-reminder`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ case_number: caseNumber, days_before: daysBefore, enabled }),
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new ApiError(error.detail || 'Nu s-a putut salva reamintirea.', response.status);
  }
  return response.json();
};

export const listFollowedCases = async () => {
  const response = await fetchWithTimeout(`${API_URL}/followed-cases`, {
    credentials: 'include'
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.detail || 'Nu s-a putut încărca lista dosarelor urmărite.', response.status);
  }

  return response.json();
};

export interface LegislativeDomainItem {
  id: string;
  label: string;
  description: string;
}

export interface LegislativePreferences {
  user_id?: string;
  user_email?: string;
  domains: string[];
  canonical_refs: string[];
  email_notifications_enabled: boolean;
  terms_accepted: boolean;
  terms_accepted_at?: string | null;
  subscribed_at?: string | null;
  available_domains: LegislativeDomainItem[];
}

export interface LegislativeNotificationItem {
  id: number;
  relation_id: string;
  canonical_ref: string;
  target_label: string;
  source_act: string;
  publication: string;
  effective_date?: string | null;
  affected_articles: string[];
  summary: string;
  modified_text_only?: string | null;
  is_read: boolean;
  read_at?: string | null;
  email_sent: boolean;
  created_at?: string | null;
}

export interface LegislativeNotificationsResponse {
  items: LegislativeNotificationItem[];
  total: number;
  unread_count: number;
  limit: number;
  offset: number;
}

export const getLegislativePreferences = async (): Promise<LegislativePreferences> => {
  const response = await fetchWithTimeout(`${API_URL}/v1/user/legislative-alerts/preferences`, {
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.detail || 'Nu s-au putut încărca preferințele legislative.', response.status);
  }
  return response.json();
};

export const saveLegislativePreferences = async (payload: {
  domains: string[];
  canonical_refs?: string[];
  email_notifications_enabled: boolean;
  terms_accepted: boolean;
}): Promise<LegislativePreferences> => {
  const response = await fetchWithTimeout(`${API_URL}/v1/user/legislative-alerts/preferences`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.detail || 'Nu s-au putut salva preferințele legislative.', response.status);
  }
  return response.json();
};

export const listLegislativeNotifications = async (params?: {
  unread_only?: boolean;
  limit?: number;
  offset?: number;
}): Promise<LegislativeNotificationsResponse> => {
  const q = new URLSearchParams();
  if (params?.unread_only) q.set('unread_only', 'true');
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.offset) q.set('offset', String(params.offset));

  const response = await fetchWithTimeout(`${API_URL}/v1/user/legislative-alerts/notifications?${q.toString()}`, {
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.detail || 'Nu s-au putut încărca modificările legislative.', response.status);
  }
  return response.json();
};

export const markLegislativeNotificationRead = async (id: number): Promise<{ success: boolean; id: number; is_read: boolean }> => {
  const response = await fetchWithTimeout(`${API_URL}/v1/user/legislative-alerts/notifications/${id}/read`, {
    method: 'PATCH',
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.detail || 'Nu s-a putut marca notificarea ca citită.', response.status);
  }
  return response.json();
};

export const markAllLegislativeNotificationsRead = async (): Promise<{ success: boolean; marked_count: number }> => {
  const response = await fetchWithTimeout(`${API_URL}/v1/user/legislative-alerts/notifications/mark-all-read`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.detail || 'Nu s-au putut marca toate notificările ca citite.', response.status);
  }
  return response.json();
};

export const unsubscribeLegislativeAlerts = async (): Promise<{ success: boolean; message: string }> => {
  const response = await fetchWithTimeout(`${API_URL}/v1/user/legislative-alerts/unsubscribe`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.detail || 'Nu s-a putut dezactiva abonarea la alertele legislative.', response.status);
  }
  return response.json();
};

// AI Cost Accounting & Pricing Admin APIs
export const getAiCostPricing = async (): Promise<AiPricingConfig> => {
  const response = await fetchWithTimeout(`${API_URL}/admin/ai-cost/pricing`, {
    method: 'GET',
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.detail || 'Nu s-a putut încărca configurarea prețurilor AI.', response.status);
  }
  return response.json();
};

export const updateAiCostPricing = async (payload: AiPricingUpdatePayload): Promise<AiPricingConfig> => {
  const response = await fetchWithTimeout(`${API_URL}/admin/ai-cost/pricing`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload),
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.detail || 'Nu s-au putut actualiza prețurile AI.', response.status);
  }
  return response.json();
};

export const getAiCostSummary = async (): Promise<AiCostSummary> => {
  const response = await fetchWithTimeout(`${API_URL}/admin/ai-cost/summary`, {
    method: 'GET',
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.detail || 'Nu s-a putut încărca sumarul costurilor AI.', response.status);
  }
  return validateAiCostSummary(await response.json());
};

export const getAdminUserAiCost = async (userId: string | number): Promise<AiUserCostDetail> => {
  const response = await fetchWithTimeout(`${API_URL}/admin/ai-cost/users/${userId}`, {
    method: 'GET',
    credentials: 'include'
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.detail || 'Nu s-a putut încărca detaliul consumului AI.', response.status);
  }
  return validateAiUserCost(await response.json());
};


